// Feedback Component - AI cevapları için geri bildirim butonları
import { useState } from "react";
import { ThumbsUp, ThumbsDown, X, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function FeedbackButtons({ 
  messageId, 
  conversationId, 
  userQuery, 
  aiResponse, 
  assistantType 
}) {
  const { t } = useLanguage();
  const [feedbackGiven, setFeedbackGiven] = useState(null); // "positive" | "negative" | null
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const NEGATIVE_REASONS = [
    { value: "wrong_info", label: t('feedback_wrong'), emoji: "❌" },
    { value: "incomplete", label: t('feedback_incomplete'), emoji: "📝" },
    { value: "unclear", label: t('feedback_unclear'), emoji: "❓" },
    { value: "off_topic", label: t('feedback_irrelevant'), emoji: "🎯" },
    { value: "too_long", label: t('feedback_slow'), emoji: "📜" },
    { value: "too_short", label: t('feedback_incomplete'), emoji: "📌" },
    { value: "other", label: t('feedback_other'), emoji: "💭" }
  ];

  const submitFeedback = async (type, reason = null, userComment = null) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        message_id: messageId,
        conversation_id: conversationId,
        feedback_type: type,
        negative_reason: reason,
        comment: userComment,
        user_query: userQuery,
        ai_response: aiResponse,
        assistant_type: assistantType
      });
      
      setFeedbackGiven(type);
    } catch (error) {
      console.error("Feedback error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePositive = () => {
    submitFeedback("positive");
  };

  const handleNegative = () => {
    setShowReasonModal(true);
  };

  const handleSubmitNegative = () => {
    if (!selectedReason) {
      return; // Sessizce return
      return;
    }
    submitFeedback("negative", selectedReason, comment);
    setShowReasonModal(false);
    setSelectedReason(null);
    setComment("");
  };

  // Eğer geri bildirim verilmişse, teşekkür göster
  if (feedbackGiven) {
    return (
      <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mt-2">
        <CheckCircle size={14} className="text-green-500" />
        <span>{t('feedback_saved')}</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1 mt-2">
        <span className="text-xs text-[var(--text-muted)] mr-2">{t('was_helpful')}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[var(--text-muted)] hover:text-green-500 hover:bg-green-500/10"
          onClick={handlePositive}
          disabled={isSubmitting}
        >
          <ThumbsUp size={14} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10"
          onClick={handleNegative}
          disabled={isSubmitting}
        >
          <ThumbsDown size={14} />
        </Button>
      </div>

      {/* Negatif Geri Bildirim Modalı */}
      <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
        <DialogContent className="bg-[var(--bg-secondary)] border-[var(--border-color)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
              <ThumbsDown size={18} className="text-red-400" />
              {t('why_unhappy')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {NEGATIVE_REASONS.map((reason) => (
                <button
                  key={reason.value}
                  onClick={() => setSelectedReason(reason.value)}
                  className={`p-3 rounded-lg border text-left text-sm transition-all ${
                    selectedReason === reason.value
                      ? "border-red-500 bg-red-500/20 text-[var(--text-primary)]"
                      : "border-[var(--border-color)] bg-[var(--bg-tertiary)]/50 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
                  }`}
                >
                  <span className="mr-2">{reason.emoji}</span>
                  {reason.label}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <label className="text-sm text-[var(--text-secondary)]">
                {t('add_comment')}
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('add_comment')}
                className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none"
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowReasonModal(false);
                  setSelectedReason(null);
                  setComment("");
                }}
                className="text-[var(--text-secondary)]"
              >
                <X size={16} className="mr-1" />
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSubmitNegative}
                disabled={!selectedReason || isSubmitting}
                className="bg-red-600 hover:bg-red-700"
              >
                <Send size={16} className="mr-1" />
                {t('send')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FeedbackButtons;
